import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  ChecklistProgress,
  ChecklistItem,
  ChecklistTemplate,
} from "../types/checklist";

const CHECKLIST_COLLECTION = "checklistProgress";

class ChecklistService {
  /**
   * Generate a unique progress ID
   */
  private generateProgressId(): string {
    return `progress_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 9)}`;
  }

  /**
   * Convert Firestore timestamp to Date
   */
  private convertTimestampToDate(timestamp: any): Date {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    return new Date(timestamp);
  }

  /**
   * Get the default checklist template
   */
  private getDefaultTemplate(): ChecklistTemplate[] {
    return [
      // Android Pre-Launch
      {
        platform: "android",
        category: "Pre-Launch",
        subcategory: "Code Preparation",
        items: [
          "Remove all debug code and logs",
          "Enable ProGuard/R8 for code obfuscation",
          "Set minifyEnabled to true in release build",
          "Configure proper signing config",
          "Update version code and version name",
          "Review app permissions (remove unnecessary ones)",
          "Test on multiple devices and Android versions",
          "Optimize APK/AAB size",
          "Set proper application ID",
        ],
      },
      {
        platform: "android",
        category: "Google Play Console Setup",
        subcategory: "Store Listing",
        items: [
          "Create app listing",
          "Upload app icon (512 x 512px)",
          "Add feature graphic (1024 x 500px)",
          "Upload screenshots (minimum 2)",
          "Write short and full description",
          "Set content rating",
          "Select app category",
          "Add privacy policy URL",
          "Configure pricing and distribution",
          "Set up developer account",
        ],
      },
      {
        platform: "android",
        category: "Technical Requirements",
        subcategory: "Configuration",
        items: [
          "Target latest API level (within 1 year)",
          "Test 64-bit support",
          "Configure App Signing by Google Play",
          "Set up Google Play Licensing (if paid)",
          "Implement proper deep linking",
          "Configure Firebase (if used)",
          "Set up crash reporting (Firebase Crashlytics)",
          "Implement analytics",
        ],
      },
      {
        platform: "android",
        category: "Security",
        subcategory: "Security Measures",
        items: [
          "Use HTTPS for all network requests",
          "Implement certificate pinning",
          "Secure storage for sensitive data",
          "Implement root detection (if needed)",
          "Obfuscate API endpoints",
          "Review AndroidManifest.xml security",
        ],
      },
      // iOS Pre-Launch
      {
        platform: "ios",
        category: "Pre-Launch",
        subcategory: "Code Preparation",
        items: [
          "Remove all debug code and print statements",
          "Archive build in Release configuration",
          "Update version and build number",
          "Configure proper code signing",
          "Test on real devices (not just simulator)",
          "Test on multiple iOS versions",
          "Optimize app size",
          "Review app permissions usage descriptions",
        ],
      },
      {
        platform: "ios",
        category: "App Store Connect Setup",
        subcategory: "Store Listing",
        items: [
          "Create app listing",
          "Upload app icon (1024 x 1024px)",
          "Add screenshots for all required device sizes",
          "Write app description",
          "Add keywords for ASO",
          "Set pricing and availability",
          "Configure App Store information",
          "Add privacy policy URL",
          "Set age rating",
        ],
      },
      {
        platform: "ios",
        category: "Technical Requirements",
        subcategory: "Configuration",
        items: [
          "Support latest iOS version",
          "Include required device capabilities",
          "Configure proper Info.plist",
          "Set up App Transport Security",
          "Implement universal links (if needed)",
          "Configure push notifications certificates",
          "Set up crash reporting (Firebase/Crashlytics)",
          "Implement analytics",
        ],
      },
      {
        platform: "ios",
        category: "App Review Guidelines",
        subcategory: "Compliance",
        items: [
          "Test all features thoroughly",
          "Provide demo account (if login required)",
          "Include App Review information",
          "Ensure no placeholder content",
          "Follow Human Interface Guidelines",
          "No private APIs used",
          "Proper error handling",
          "Appropriate content rating",
        ],
      },
      {
        platform: "ios",
        category: "Security",
        subcategory: "Security Measures",
        items: [
          "Use HTTPS for all network requests",
          "Implement certificate pinning",
          "Secure storage with Keychain",
          "Implement jailbreak detection (if needed)",
          "Review Info.plist security settings",
          "Protect sensitive API endpoints",
        ],
      },
      // Cross-Platform
      {
        platform: "cross-platform",
        category: "General",
        subcategory: "Functionality",
        items: [
          "Test payment flows (if applicable)",
          "Verify deep linking works",
          "Test share functionality",
          "Check in-app links",
          "Verify third-party SDK integrations",
          "Test offline functionality",
          "Review onboarding flow",
          "Check localization/translations",
        ],
      },
      {
        platform: "cross-platform",
        category: "Performance",
        subcategory: "Optimization",
        items: [
          "App launches in < 3 seconds",
          "No memory leaks",
          "Optimized image assets",
          "Lazy loading implemented",
          "Database queries optimized",
          "Network requests cached appropriately",
        ],
      },
      {
        platform: "cross-platform",
        category: "Legal & Compliance",
        subcategory: "Legal Documents",
        items: [
          "Terms of Service in place",
          "Privacy Policy published",
          "GDPR compliance (if targeting EU)",
          "COPPA compliance (if targeting children)",
          "Cookie consent (if applicable)",
          "Data deletion process",
        ],
      },
      {
        platform: "cross-platform",
        category: "Post-Launch",
        subcategory: "Monitoring",
        items: [
          "Monitor crash reports",
          "Track key metrics",
          "Respond to user reviews",
          "Plan first update",
          "Monitor server performance",
          "Set up alerts for critical issues",
        ],
      },
    ];
  }

  /**
   * Initialize checklist for an app
   */
  private initializeChecklist(): ChecklistItem[] {
    const templates = this.getDefaultTemplate();
    const items: ChecklistItem[] = [];

    templates.forEach((template) => {
      template.items.forEach((itemText, index) => {
        items.push({
          id: `${template.platform}_${template.category}_${template.subcategory}_${index}`,
          text: itemText,
          completed: false,
          category: template.category,
          subcategory: template.subcategory,
          platform: template.platform,
        });
      });
    });

    return items;
  }

  /**
   * Calculate progress percentage
   */
  private calculateProgress(items: ChecklistItem[], platform?: string): number {
    const relevantItems = platform
      ? items.filter((item) => item.platform === platform)
      : items;

    if (relevantItems.length === 0) return 0;

    const completedCount = relevantItems.filter(
      (item) => item.completed,
    ).length;
    return Math.round((completedCount / relevantItems.length) * 100);
  }

  /**
   * Get checklist progress for an app
   */
  async getChecklistProgress(
    appId: string,
    userId: string,
  ): Promise<ChecklistProgress | null> {
    try {
      const progressRef = collection(db, CHECKLIST_COLLECTION);
      const q = query(
        progressRef,
        where("appId", "==", appId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const data = querySnapshot.docs[0].data();
      return {
        progressId: data.progressId,
        appId: data.appId,
        userId: data.userId,
        items: data.items,
        lastUpdated: this.convertTimestampToDate(data.lastUpdated),
        overallProgress: data.overallProgress,
        androidProgress: data.androidProgress,
        iosProgress: data.iosProgress,
        crossPlatformProgress: data.crossPlatformProgress,
      } as ChecklistProgress;
    } catch (error) {
      console.error("Error fetching checklist progress:", error);
      throw new Error("Failed to fetch checklist progress");
    }
  }

  /**
   * Create or get checklist progress for an app
   */
  async getOrCreateProgress(
    appId: string,
    userId: string,
  ): Promise<ChecklistProgress> {
    try {
      const existing = await this.getChecklistProgress(appId, userId);

      if (existing) {
        return existing;
      }

      // Create new progress
      const progressId = this.generateProgressId();
      const items = this.initializeChecklist();

      const newProgress = {
        progressId,
        appId,
        userId,
        items,
        lastUpdated: serverTimestamp(),
        overallProgress: 0,
        androidProgress: 0,
        iosProgress: 0,
        crossPlatformProgress: 0,
      };

      await addDoc(collection(db, CHECKLIST_COLLECTION), newProgress);

      return {
        ...newProgress,
        lastUpdated: new Date(),
      } as ChecklistProgress;
    } catch (error) {
      console.error("Error creating checklist progress:", error);
      throw new Error("Failed to create checklist progress");
    }
  }

  /**
   * Update checklist item completion status
   */
  async updateItemStatus(
    appId: string,
    userId: string,
    itemId: string,
    completed: boolean,
  ): Promise<void> {
    try {
      const progressRef = collection(db, CHECKLIST_COLLECTION);
      const q = query(
        progressRef,
        where("appId", "==", appId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("Progress not found");
      }

      const docRef = querySnapshot.docs[0].ref;
      const data = querySnapshot.docs[0].data();
      const items: ChecklistItem[] = data.items;

      // Update the specific item
      const updatedItems = items.map((item) =>
        item.id === itemId ? { ...item, completed } : item,
      );

      // Recalculate progress
      const overallProgress = this.calculateProgress(updatedItems);
      const androidProgress = this.calculateProgress(updatedItems, "android");
      const iosProgress = this.calculateProgress(updatedItems, "ios");
      const crossPlatformProgress = this.calculateProgress(
        updatedItems,
        "cross-platform",
      );

      await updateDoc(docRef, {
        items: updatedItems,
        lastUpdated: serverTimestamp(),
        overallProgress,
        androidProgress,
        iosProgress,
        crossPlatformProgress,
      });
    } catch (error) {
      console.error("Error updating item status:", error);
      throw new Error("Failed to update item status");
    }
  }

  /**
   * Reset checklist for an app
   */
  async resetChecklist(appId: string, userId: string): Promise<void> {
    try {
      const progressRef = collection(db, CHECKLIST_COLLECTION);
      const q = query(
        progressRef,
        where("appId", "==", appId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("Progress not found");
      }

      const docRef = querySnapshot.docs[0].ref;
      const items = this.initializeChecklist();

      await updateDoc(docRef, {
        items,
        lastUpdated: serverTimestamp(),
        overallProgress: 0,
        androidProgress: 0,
        iosProgress: 0,
        crossPlatformProgress: 0,
      });
    } catch (error) {
      console.error("Error resetting checklist:", error);
      throw new Error("Failed to reset checklist");
    }
  }
}

export default new ChecklistService();
